import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { AppContext } from '../../../App'
import { ChartEvents } from '../chart.events'
import { ExtraWideTooltip } from '../../custom'
import {
    Box,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material'
import { DataRenderType, OiProfileDataType, OiProfileStateType } from '../drawingTools/oiProfile/oiProfile'
import { createStringDateFromStamp } from '../../../util/tradePage'
import { Expiry } from '../../../proto/generated/common'
import { formatOiQuantity } from '../../../util/screener'
import { green, red } from '../../../assets/colors'
import { DrawingSettings, DrawingTypes } from '../drawingTools/drawing.interface'
import { useSelector } from 'react-redux'
import { selectChartConfig } from '../../../selectors/chart'
import { getSecurityAsync } from '../../../util/security'

export type OiProfileTooltipProps = {
    chartId: string
}

export type OiHeatmapDataType = {
    strike: number,
    call: number | undefined,
    put: number | undefined,
}

export type TooltipData<T> = {
    type: DrawingTypes,
    x: number,
    y: number,
    settings: DrawingSettings<any>
} & T

export type OiProfileTooltipData = TooltipData<{
    state: OiProfileStateType,
    renderType: DataRenderType,
    cumulativeData: Map<Expiry | undefined, OiProfileDataType>
}>

export type OiHeatmapTooltipData = TooltipData<{
    selectedStrike?: number,
    strikes: OiHeatmapDataType[]
}>

const OiProfileTooltip = ({ chartId }: OiProfileTooltipProps) => {
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(chartId)
    const chartConfig = useSelector(selectChartConfig(chartId))
    const [data, setData] = useState<OiProfileTooltipData | OiHeatmapTooltipData>()

    const securityKey = useMemo(() => {
        const primaryGrp = Object.values(chartConfig.seriesGroups).find(v => v.primary);
        return primaryGrp?.securityList?.[0] ?? null
    }, [chartConfig])
    const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => { if (securityKey) getSecurityAsync(securityKey).then(setSecurity); else setSecurity(undefined) }, [securityKey])

    const onDrawingHovered = useCallback((event: CustomEvent) => {
        setData(event.detail)
    }, [])

    useEffect(() => {
        chart && chart.subscribeEvent(ChartEvents.DRAWING_HOVERED, onDrawingHovered)
        return () => {
            chart && chart.unsubscribeEvent(ChartEvents.DRAWING_HOVERED, onDrawingHovered)
        }
    }, [chart])


    return (data && (data.type === 'oiProfile')) ? (
        <ExtraWideTooltip open={true} title={(
            <Paper elevation={16}>
                <TableContainer>
                    <Table aria-label="cumulative data table">
                        <TableHead sx={{ borderBottom: (theme) => `0.1px solid ${theme.palette.divider}` }}>
                            <TableRow>
                                <TableCell style={{ padding: '4px 16px' }} align="right"><Typography
                                    variant={'h6'}>{(data as OiProfileTooltipData).state?.strike}</Typography></TableCell>
                                <TableCell style={{ padding: '4px 16px' }} align="right"><Typography
                                    variant={'body2'} color={'textSecondary'}>
                                    CALL
                                </Typography></TableCell>
                                <TableCell style={{ padding: '4px 16px' }} align="right"><Typography
                                    variant={'body2'} color={'textSecondary'}>
                                    PUT
                                </Typography></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.from(((data as OiProfileTooltipData).cumulativeData as Map<Expiry | undefined, OiProfileDataType>).entries()).map(([expiry, oiData], index, array) => (
                                <TableRow key={index}>
                                    <TableCell
                                        sx={{ borderBottom: (theme) => index === array.length - 1 ? 'none' : `0.1px solid ${theme.palette.divider}` }}
                                        align="right"
                                        style={{ padding: '4px 16px' }}
                                    >
                                        {createStringDateFromStamp(expiry?.expiry ?? 0)}
                                    </TableCell>
                                    <TableCell
                                        sx={{ borderBottom: (theme) => index === array.length - 1 ? 'none' : `0.1px solid ${theme.palette.divider}` }}
                                        style={{ padding: '4px 16px' }}
                                        align="right"
                                    >
                                        <Stack direction={'column'}>
                                            <Typography variant="body1">
                                                {formatOiQuantity(oiData?.callOpenInterest!)}
                                            </Typography>
                                            <Typography variant="body2" style={{
                                                color: oiData?.callOpenInterestChange ? (oiData?.callOpenInterestChange > 0 ? green : (oiData?.callOpenInterestChange < 0 ? red : 'inherit')) : 'inherit'
                                            }}>
                                                {formatOiQuantity(oiData?.callOpenInterestChange!, true)}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell
                                        sx={{ borderBottom: (theme) => index === array.length - 1 ? 'none' : `0.1px solid ${theme.palette.divider}` }}
                                        style={{ padding: '4px 16px' }}
                                        align="right"
                                    >
                                        <Stack>
                                            <Typography variant="body1">
                                                {formatOiQuantity(oiData?.putOpenInterest!)}
                                            </Typography>
                                            <Typography variant="body2" style={{
                                                color: oiData?.putOpenInterestChange ? (oiData?.putOpenInterestChange > 0 ? green : (oiData?.putOpenInterestChange < 0 ? red : 'inherit')) : 'inherit'
                                            }}>
                                                {formatOiQuantity(oiData?.putOpenInterestChange!, true)}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        )} sx={{ margin: '0px !important', padding: '0px !important', pointerEvents: 'none' }}>
            <Box sx={{ position: 'absolute', left: data.x, top: data.y + 15 }} />
        </ExtraWideTooltip>
    ) : (data && (data.type === 'oiHeatmap') && (data as OiHeatmapTooltipData).strikes.length > 0) ? (
        <ExtraWideTooltip open={true} title={(
            <Paper elevation={16}>
                <TableContainer>
                    <Table aria-label="cumulative data table">
                        <TableHead sx={{ borderBottom: (theme) => `0.1px solid ${theme.palette.divider}` }}>
                            <TableRow>
                                <TableCell style={{ padding: '4px 16px' }} align="right"><Typography
                                    variant={'body2'} color={'textSecondary'}>
                                    STRIKE
                                </Typography></TableCell>
                                <TableCell style={{ padding: '4px 16px' }} align="right"><Typography
                                    variant={'body2'} color={'textSecondary'}>
                                    CALL
                                </Typography></TableCell>
                                <TableCell style={{ padding: '4px 16px' }} align="right"><Typography
                                    variant={'body2'} color={'textSecondary'}>
                                    PUT
                                </Typography></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {((data as OiHeatmapTooltipData)).strikes.map((row: OiHeatmapDataType, index: number, array: OiHeatmapDataType[]) => (
                                <TableRow key={row.strike} selected={row.strike === (data as OiHeatmapTooltipData).selectedStrike}>
                                    <TableCell
                                        sx={{ borderBottom: (theme) => index === array.length - 1 ? 'none' : `0.1px solid ${theme.palette.divider}` }}
                                        align="right"
                                        style={{ padding: '4px 16px' }}
                                    >
                                        {(row.strike/(security?.priceMultiplier??100)).toLocaleString('en-IN')}
                                    </TableCell>
                                    <TableCell
                                        sx={{ borderBottom: (theme) => index === array.length - 1 ? 'none' : `0.1px solid ${theme.palette.divider}` }}
                                        align="right"
                                        style={{ padding: '4px 16px' }}
                                    >
                                        <Typography variant="body2" style={{
                                            color: row.call ? (row.call > 0 ? green : red) : 'inherit'
                                        }}>
                                            {row.call ? row.call.toLocaleString('en-IN') : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell
                                        sx={{ borderBottom: (theme) => index === array.length - 1 ? 'none' : `0.1px solid ${theme.palette.divider}` }}
                                        align="right"
                                        style={{ padding: '4px 16px' }}
                                    >
                                        <Typography variant="body2" style={{
                                            color: row.put ? (row.put > 0 ? green : red) : 'inherit'
                                        }}>
                                            {row.put ? row.put.toLocaleString('en-IN') : '-'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        )} sx={{ margin: 0, padding: 0, pointerEvents: 'none' }}>
            <Box sx={{ position: 'absolute', left: data.x, top: data.y + 15 }} />
        </ExtraWideTooltip>
    ) : null
}

export default OiProfileTooltip